/**
 * Activity Calendar
 * Monthly calendar showing daily activity levels
 */

import { useState } from 'react';
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { generateCalendarMonth, getMonthName } from '../utils/helpers';
import { ACTIVITY_COLORS } from '../utils/constants';
import type { CalendarMonth, CalendarDay } from '../types';

interface ActivityCalendarProps {
  activityData?: Record<string, number>;
}

function DayCell({ day }: { day: CalendarDay }) {
  const bgColor = day.isCurrentMonth
    ? ACTIVITY_COLORS[day.activityLevel]
    : 'bg-transparent';

  return (
    <div
      className={`aspect-square flex items-center justify-center rounded-lg text-sm transition-all ${
        day.isCurrentMonth
          ? day.isToday
            ? 'ring-2 ring-primary-500 ring-offset-2'
            : ''
          : 'text-gray-300'
      } ${bgColor}`}
      title={day.xpEarned ? `${day.xpEarned} XP earned` : undefined}
    >
      <span
        className={`${
          day.isCurrentMonth
            ? day.activityLevel === 'high' || day.activityLevel === 'medium'
              ? 'text-white font-medium'
              : 'text-gray-700'
            : 'text-gray-300'
        }`}
      >
        {day.dayOfMonth}
      </span>
    </div>
  );
}

export function ActivityCalendar({ activityData }: ActivityCalendarProps) {
  const { t } = useLanguage();
  const today = new Date();
  const [currentMonth, setCurrentMonth] = useState<CalendarMonth>(() =>
    generateCalendarMonth(today.getFullYear(), today.getMonth(), activityData)
  );

  const goToPreviousMonth = () => {
    const prevMonth = currentMonth.month === 0 ? 11 : currentMonth.month - 1;
    const prevYear = currentMonth.month === 0 ? currentMonth.year - 1 : currentMonth.year;
    setCurrentMonth(generateCalendarMonth(prevYear, prevMonth, activityData));
  };

  const goToNextMonth = () => {
    const nextMonth = currentMonth.month === 11 ? 0 : currentMonth.month + 1;
    const nextYear = currentMonth.month === 11 ? currentMonth.year + 1 : currentMonth.year;
    setCurrentMonth(generateCalendarMonth(nextYear, nextMonth, activityData));
  };

  const goToToday = () => {
    setCurrentMonth(generateCalendarMonth(today.getFullYear(), today.getMonth(), activityData));
  };

  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-lg hover:border-primary-200 transition-all duration-200">
      <div className="px-6 py-4 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-100 rounded-lg">
              <Calendar className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {t('challenges.calendar.title', 'Activity Calendar')}
              </h2>
              <p className="text-sm text-gray-500">
                {t('challenges.calendar.subtitle', 'Track your daily progress')}
              </p>
            </div>
          </div>

          <button
            onClick={goToToday}
            className="text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            {t('challenges.calendar.today', 'Today')}
          </button>
        </div>
      </div>

      <div className="p-6">
        {/* Month Navigation */}
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={goToPreviousMonth}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label={t('challenges.calendar.prevMonth', 'Previous month')}
          >
            <ChevronLeft className="w-5 h-5 text-gray-600" />
          </button>

          <h3 className="text-lg font-semibold text-gray-900">
            {getMonthName(currentMonth.month)} {currentMonth.year}
          </h3>

          <button
            onClick={goToNextMonth}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label={t('challenges.calendar.nextMonth', 'Next month')}
          >
            <ChevronRight className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Week Day Headers */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {weekDays.map((day) => (
            <div
              key={day}
              className="text-center text-xs font-medium text-gray-500 py-2"
            >
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-1">
          {currentMonth.days.map((day, index) => (
            <DayCell key={index} day={day} />
          ))}
        </div>

        {/* Legend */}
        <div className="mt-4 pt-4 border-t border-gray-100">
          <div className="flex items-center justify-center gap-4 text-xs text-gray-500">
            <div className="flex items-center gap-1.5">
              <div className={`w-3 h-3 rounded ${ACTIVITY_COLORS.none}`} />
              <span>{t('challenges.calendar.legend.none', 'No activity')}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className={`w-3 h-3 rounded ${ACTIVITY_COLORS.low}`} />
              <span>{t('challenges.calendar.legend.low', 'Low')}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className={`w-3 h-3 rounded ${ACTIVITY_COLORS.medium}`} />
              <span>{t('challenges.calendar.legend.medium', 'Medium')}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className={`w-3 h-3 rounded ${ACTIVITY_COLORS.high}`} />
              <span>{t('challenges.calendar.legend.high', 'High')}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
